import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import type { HandbookFilters as HandbookFiltersType } from "@/types/handbook";

interface HandbookFiltersProps {
  filters: HandbookFiltersType;
  onFiltersChange: (filters: HandbookFiltersType) => void;
}

export function HandbookFilters({
  filters,
  onFiltersChange,
}: HandbookFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search ?? "");
  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    const normalizedSearch = debouncedSearch.trim() || undefined;
    if (normalizedSearch !== filters.search) {
      onFiltersChange({ ...filters, search: normalizedSearch });
    }
  }, [debouncedSearch, filters, onFiltersChange]);

  const hasActiveFilters = Boolean(
    filters.status || filters.tag || filters.search
  );

  const clearFilters = () => {
    setSearchInput("");
    onFiltersChange({});
  };

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center">
      <div className="relative w-full md:w-[240px]">
        <Search className="text-muted-foreground absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2" />
        <Input
          type="text"
          placeholder="제목으로 검색"
          className="w-full pl-8"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="제목 검색"
        />
      </div>

      <Select
        value={filters.status || "ALL"}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            status:
              value === "ALL"
                ? undefined
                : (value as "draft" | "published"),
          })
        }
      >
        <SelectTrigger
          className="w-full md:w-[140px]"
          aria-label="상태 필터"
        >
          <SelectValue placeholder="상태 선택" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">전체 상태</SelectItem>
          <SelectItem value="draft">임시저장</SelectItem>
          <SelectItem value="published">발행됨</SelectItem>
        </SelectContent>
      </Select>

      <Input
        type="text"
        placeholder="태그로 필터"
        className="w-full md:w-[200px]"
        value={filters.tag ?? ""}
        onChange={(e) =>
          onFiltersChange({
            ...filters,
            tag: e.target.value || undefined,
          })
        }
        aria-label="태그 필터"
      />

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className="text-muted-foreground"
        >
          <X className="mr-1 h-4 w-4" />
          필터 초기화
        </Button>
      )}
    </div>
  );
}
